import { useEffect, useState } from "react";
import { X, Send, AlertTriangle } from "react-feather";
import { format } from "date-fns";
import {
  brandService,
  BulkInviteCampaignsOverview,
  BulkInviteCampaignStatus,
} from "../../services/brand.service";

interface BulkInviteStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_LABELS: Record<BulkInviteCampaignStatus, string> = {
  QUEUED: "Queued",
  SENDING: "Sending",
  WAITING: "Waiting",
  HALTED: "Halted",
  COMPLETED: "Completed",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

function statusBadgeClasses(status: BulkInviteCampaignStatus): string {
  switch (status) {
    case "COMPLETED":
      return "bg-green-100 text-green-800";
    case "SENDING":
    case "WAITING":
    case "QUEUED":
      return "bg-blue-100 text-blue-800";
    case "HALTED":
      return "bg-yellow-100 text-yellow-800";
    case "CANCELLED":
      return "bg-gray-200 text-muted-foreground";
    case "FAILED":
      return "bg-red-100 text-red-800";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <div className="border border-border rounded-lg p-3 text-center">
      <div className={`text-xl font-semibold ${accent || "text-foreground"}`}>
        {value.toLocaleString()}
      </div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

export function BulkInviteStatsModal({ isOpen, onClose }: BulkInviteStatsModalProps) {
  const [data, setData] = useState<BulkInviteCampaignsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    brandService
      .getBulkInviteCampaigns()
      .then(setData)
      .catch(() => setError("Failed to load bulk invite stats"))
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-card rounded-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-brand-100 rounded-full flex items-center justify-center">
              <Send className="h-5 w-5 text-brand-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Bulk Invite Stats</h2>
              <p className="text-sm text-muted-foreground">
                Your bulk affiliate invite import history
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="text-muted-foreground hover:text-muted-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Loading stats…
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-red-600">{error}</div>
          ) : !data || data.campaigns.length === 0 ? (
            <div className="py-12 text-center">
              <div className="flex justify-center mb-3">
                <div className="p-3 bg-brand-100 rounded-full">
                  <Send className="h-6 w-6 text-brand-600" />
                </div>
              </div>
              <h3 className="text-base font-medium text-foreground mb-1">
                No bulk imports yet
              </h3>
              <p className="text-sm text-muted-foreground">
                When you bulk-invite affiliates via CSV/Excel import, stats will show
                up here.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                <StatCard label="Imports" value={data.totals.campaignCount} />
                <StatCard
                  label="Total recipients"
                  value={data.totals.totalRecipients}
                />
                <StatCard
                  label="Sent"
                  value={data.totals.sentCount}
                  accent="text-green-700"
                />
                <StatCard
                  label="Bounced"
                  value={data.totals.bouncedCount}
                  accent="text-yellow-700"
                />
                <StatCard
                  label="Failed"
                  value={data.totals.failedCount}
                  accent="text-red-700"
                />
                <StatCard
                  label="Cancelled"
                  value={data.totals.cancelledCount}
                  accent="text-muted-foreground"
                />
              </div>

              <div className="space-y-3">
                {data.campaigns.map((c) => (
                  <div key={c.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClasses(
                            c.status
                          )}`}
                        >
                          {STATUS_LABELS[c.status] || c.status}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(c.createdAt), "MMM dd, yyyy HH:mm")}
                        </span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {c.sentCount}/{c.totalRecipients} sent
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span>Bounced: {c.bouncedCount}</span>
                      <span>Failed: {c.failedCount}</span>
                      <span>Cancelled: {c.cancelledCount}</span>
                    </div>
                    {c.haltReason && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        {c.haltReason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
